import React, { useState } from 'react';
import { X, Check, Phone, ArrowRight } from 'lucide-react';
import { business } from '../mock';
import { createConsultation } from '../api';

const careOptions = ['Personal Care', 'Home Care', '24-Hour / Overnight', 'Dementia / Alzheimer\u2019s', 'Parkinson\u2019s Care', 'Memory Care', 'Not sure yet'];

export default function ConsultationModal({ open, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', careType: '', relation: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await createConsultation(form);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please call us and we\u2019ll help right away.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(20,42,31,0.55)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-[560px] max-h-[92vh] overflow-y-auto" style={{ boxShadow: '0 30px 80px -20px rgba(0,0,0,0.4)' }}>
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 flex items-center justify-center rounded-full z-10" style={{ width: 38, height: 38, backgroundColor: 'var(--cream-2)' }}>
          <X size={20} style={{ color: 'var(--forest)' }} />
        </button>

        {submitted ? (
          <div className="p-10 text-center">
            <span className="flex items-center justify-center rounded-full mx-auto mb-6" style={{ width: 72, height: 72, backgroundColor: 'var(--sage)' }}>
              <Check size={38} style={{ color: 'var(--forest)' }} strokeWidth={2.5} />
            </span>
            <h3 className="text-2xl">Thank you, {form.name.split(' ')[0] || 'friend'}!</h3>
            <p className="mt-3" style={{ color: 'var(--muted)' }}>
              We&rsquo;ve received your request for a free, no-obligation consultation. A care
              specialist will reach out shortly. For immediate help, call us anytime.
            </p>
            <a href={business.phoneHref} className="btn-sage mt-7">
              <Phone size={18} /> Call {business.phone}
            </a>
          </div>
        ) : (
          <div className="p-8 lg:p-10">
            <p className="eyebrow mb-1">Free &amp; No Obligation</p>
            <h3 className="text-2xl">Schedule a Care Consultation</h3>
            <p className="mt-2 text-[15px]" style={{ color: 'var(--muted)' }}>
              Tell us a little about your needs and we&rsquo;ll be in touch. Your information is kept private.
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input required value={form.name} onChange={(e) => set('name', e.target.value)} className="inp" name="name" data-testid="consult-name-input" />
                </Field>
                <Field label="Phone" required>
                  <input required value={form.phone} onChange={(e) => set('phone', e.target.value)} className="inp" name="phone" data-testid="consult-phone-input" />
                </Field>
              </div>
              <Field label="Email">
                <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="inp" name="email" data-testid="consult-email-input" />
              </Field>
              <Field label="What kind of care are you exploring?">
                <div className="flex flex-wrap gap-2">
                  {careOptions.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => set('careType', c)}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                      style={{
                        backgroundColor: form.careType === c ? 'var(--forest)' : 'var(--cream-2)',
                        color: form.careType === c ? '#fff' : 'var(--ink)',
                        border: `1px solid ${form.careType === c ? 'var(--forest)' : 'var(--line)'}`,
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Anything you'd like us to know?">
                <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="Tell us about your loved one's situation..." className="inp" style={{ resize: 'none' }} name="notes" data-testid="consult-notes-input" />
              </Field>

              <button type="submit" disabled={loading} className="btn-sage justify-center mt-1" style={{ opacity: loading ? 0.7 : 1 }} data-testid="consult-submit-btn">
                {loading ? 'Sending\u2026' : 'Request My Free Consultation'} <ArrowRight size={18} />
              </button>
              {error && <p className="text-sm text-center" style={{ color: '#B4614A' }}>{error}</p>}
            </form>
          </div>
        )}
      </div>

      <style>{`
        .inp {
          width: 100%;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid var(--line);
          background: var(--cream);
          color: var(--ink);
          font-size: 15px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .inp:focus { border-color: var(--forest); box-shadow: 0 0 0 3px rgba(30,59,44,0.1); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--forest)' }}>
        {label} {required && <span style={{ color: '#B4614A' }}>*</span>}
      </span>
      {children}
    </label>
  );
}
